import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { formatUnixDateTime } from '@/shared/lib/datetime';
import { Pagination } from '@/shared/ui/Pagination';
import { QueryStatus } from '@/shared/ui/QueryStatus';
import {
  extendAnswerTime,
  setBidService,
  type BidOfferRow,
  type BidServicesKind,
  type BidServiceRef,
  type BidViewerRow,
} from '../detailApi';
import { formatWorkTypes } from './format';
import { PhoneCell } from './PhoneCell';
import { useBidAction, useBidServices } from './useBidAction';

type TableProps<T extends Record<string, unknown>> = {
  title: string;
  kind: BidServicesKind;
  bidId: number;
  headers: string[];
  renderRow: (row: T) => ReactNode;
};

/** Paginated services table shared by the three legacy blocks («с ценой», «без ответа», «видели заявку»). */
function BidServicesTable<T extends Record<string, unknown>>({
  title,
  kind,
  bidId,
  headers,
  renderRow,
}: TableProps<T>) {
  const [page, setPage] = useState(1);
  const { data, isLoading, error } = useBidServices<T>(kind, bidId, page);

  return (
    <div className="box">
      <div className="box-header with-border">
        <h3 className="box-title">{title}</h3>
      </div>
      <div className="box-body">
        <QueryStatus isLoading={isLoading} error={error} errorText="Ошибка загрузки данных" />
        <table className="table table-bordered">
          <thead>
            <tr>
              {headers.map((header) => (
                <th key={header}>{header}</th>
              ))}
            </tr>
          </thead>
          <tbody>{data?.rows.map(renderRow)}</tbody>
        </table>
        <Pagination page={page} totalPages={data?.totalPages ?? 1} onChange={setPage} />
      </div>
    </div>
  );
}

const ServiceLink = ({ service }: { service?: BidServiceRef }) =>
  service?.id ? <Link to={`/users/user/${service.id}`}>{service.name || 'не указан'}</Link> : <>не указан</>;

const rowClass = (currentServiceId: number | null | undefined, rowServiceId: number | string | undefined) =>
  currentServiceId && Number(rowServiceId) === Number(currentServiceId) ? 'bg-current' : undefined;

const ExtendTimeButton = ({ bidId, serviceId }: { bidId: number; serviceId: number | string }) => {
  const extend = useBidAction<void>(bidId, () => extendAnswerTime(bidId, serviceId));
  return (
    <>
      <button type="button" className="btn btn-primary btn-sm" disabled={extend.isPending} onClick={() => extend.run()}>
        Продлить
      </button>
      {extend.error && <span className="text-red"> {extend.error}</span>}
    </>
  );
};

const MoveToServiceButton = ({ bidId, serviceId }: { bidId: number; serviceId: number | string }) => {
  const move = useBidAction<void>(bidId, () => setBidService(bidId, serviceId));
  return (
    <>
      <button type="button" className="btn btn-success" disabled={move.isPending} onClick={() => move.run()}>
        Перевести заявку в этот сервис
      </button>
      {move.error && <span className="text-red"> {move.error}</span>}
    </>
  );
};

type SectionProps = {
  bidId: number;
  serviceId: number | null | undefined;
  createTime: number | undefined;
  finalDate?: number;
};

export const OffersTable = ({ bidId, serviceId, createTime, finalDate }: SectionProps) => (
  <BidServicesTable<BidOfferRow>
    title="Текущий СТО с указанием цены"
    kind="withCost"
    bidId={bidId}
    headers={[
      'id',
      'Название СТО',
      'Телефон',
      'Дата создания',
      'Расстояние от точки создания до СТО',
      'Стоимость',
      'Дата записи',
      'Остаточное время',
      'Продлить время',
    ]}
    renderRow={({ service, cost, distance, time_to_answer: timeToAnswer }) => (
      <tr key={String(service?.id)} className={rowClass(serviceId, service?.id)}>
        <td>{service?.id ?? 'нет айди'}</td>
        <td>
          <ServiceLink service={service} />
        </td>
        <td>
          <PhoneCell userId={service?.id} phone={service?.phone} />
        </td>
        <td>{formatUnixDateTime(createTime) || 'нет даты создания'}</td>
        <td>{distance}</td>
        <td>{cost?.cost ?? ''}</td>
        <td>{formatUnixDateTime(finalDate) || 'нет даты записи'}</td>
        <td>{formatUnixDateTime(timeToAnswer) || 'не продлён'}</td>
        <td>{service?.id && <ExtendTimeButton bidId={bidId} serviceId={service.id} />}</td>
      </tr>
    )}
  />
);

export const ViewedWithoutOfferTable = ({ bidId, serviceId }: SectionProps) => (
  <BidServicesTable<BidViewerRow>
    title="СТО просмотрели, но не ответили"
    kind="withoutCost"
    bidId={bidId}
    headers={[
      'id',
      'Название СТО',
      'Дата, время просмотра',
      'Телефон',
      'Дата создания',
      'Расстояние от точки создания до СТО',
      'Стоимость',
      'Тип работ',
    ]}
    renderRow={({ service_id: id, service }) => (
      <tr key={String(id)} className={rowClass(serviceId, service?.id)}>
        <td>{id ?? 'нет айди'}</td>
        <td>
          <ServiceLink service={service} />
        </td>
        <td>{formatUnixDateTime(service?.update_time) || 'нет даты просмотра'}</td>
        <td>{service?.phone || 'Нет номера телефона'}</td>
        <td>{formatUnixDateTime(service?.create_time) || 'нет даты создания'}</td>
        <td>{service?.distance}</td>
        <td>{service?.price}</td>
        <td>{formatWorkTypes(service?.work_type_prices)}</td>
      </tr>
    )}
  />
);

export const WhoSawBidTable = ({ bidId, serviceId }: SectionProps) => (
  <BidServicesTable<BidViewerRow>
    title="Все СТО, кому распределена заявка"
    kind="whoSee"
    bidId={bidId}
    headers={[
      'id',
      'Название СТО',
      'Телефон',
      'Дата создания',
      'Расстояние от точки создания до СТО',
      'Стоимость',
      'Дата, время ответа',
      'Тип работ',
      'Остаточное время',
      '',
    ]}
    renderRow={({ service_id: id, createdAt, service }) => (
      <tr key={String(id)} className={rowClass(serviceId, id)}>
        <td>{id}</td>
        <td>{id ? <Link to={`/users/user/${id}`}>{service?.name || 'Нет названия'}</Link> : service?.name}</td>
        <td>{service?.phone || 'Нет номера телефона'}</td>
        <td>{formatUnixDateTime(service?.create_time)}</td>
        <td>{service?.distance}</td>
        <td>{service?.price}</td>
        <td>{formatUnixDateTime(createdAt) || 'нет даты ответа'}</td>
        <td>{formatWorkTypes(service?.work_type_prices)}</td>
        <td>{formatUnixDateTime(service?.time_to_answer) || 'нет даты'}</td>
        <td>{id && <MoveToServiceButton bidId={bidId} serviceId={id} />}</td>
      </tr>
    )}
  />
);
